import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { walletService } from '../services/walletService';

// ── Icons ──────────────────────────────────────────────────────────────────────
const WalletIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
);

const XCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
);

const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
);

// ── Helpers ────────────────────────────────────────────────────────────────────
const statusBadge = (status) => {
    if (status === 'ACTIVE') return <span className="badge badge-success gap-1"><CheckCircleIcon />Active</span>;
    if (status === 'FROZEN') return <span className="badge badge-error gap-1"><XCircleIcon />Frozen</span>;
    return <span className="badge badge-ghost">{status}</span>;
};

const balanceColor = (balance) => {
    if (balance > 50) return 'text-success';
    if (balance > 0) return 'text-warning';
    return 'text-error';
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const AiWallet = () => {
    const { user } = useAuth();
    const userId = user?.userId;

    const [wallet, setWallet] = useState(null);
    const [canSpend, setCanSpend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Credit form state
    const [creditAmount, setCreditAmount] = useState('');
    const [creditLoading, setCreditLoading] = useState(false);
    const [creditSuccess, setCreditSuccess] = useState(null);
    const [creditError, setCreditError] = useState(null);

    const fetchWallet = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        try {
            const [walletData, canSpendData] = await Promise.all([
                walletService.getWallet(userId),
                walletService.canSpend(userId),
            ]);
            setWallet(walletData);
            setCanSpend(canSpendData);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to load wallet');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchWallet();
    }, [fetchWallet]);

    const handleCredit = async (e) => {
        e.preventDefault();
        const credits = parseInt(creditAmount, 10);
        if (!credits || credits <= 0) {
            setCreditError('Please enter a valid credit amount');
            return;
        }
        setCreditLoading(true);
        setCreditError(null);
        setCreditSuccess(null);
        try {
            // Generate a unique paymentId for idempotency
            const paymentId = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            await walletService.creditWallet(userId, credits, paymentId);
            setCreditSuccess(`Successfully added ${credits} credits!`);
            setCreditAmount('');
            // Refresh wallet data
            await fetchWallet();
        } catch (err) {
            setCreditError(err?.response?.data?.message || err.message || 'Failed to add credits');
        } finally {
            setCreditLoading(false);
        }
    };

    // ── Loading ──────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex justify-center items-center min-h-[400px]">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="alert alert-error">
                    <XCircleIcon />
                    <span>{error}</span>
                    <button className="btn btn-sm btn-ghost" onClick={fetchWallet}>Retry</button>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6 max-w-4xl">

                {/* ── Page Header ── */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-primary/10 text-primary">
                            <WalletIcon />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">AI Wallet</h1>
                            <p className="text-sm text-base-content/60">Manage your AI credit balance</p>
                        </div>
                    </div>
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={fetchWallet}
                        title="Refresh"
                    >
                        <RefreshIcon />
                        Refresh
                    </button>
                </div>

                {/* ── Balance Stats ── */}
                <div className="stats stats-vertical lg:stats-horizontal shadow w-full bg-base-100">
                    {/* Balance */}
                    <div className="stat">
                        <div className="stat-figure text-primary">
                            <WalletIcon />
                        </div>
                        <div className="stat-title">Credit Balance</div>
                        <div className={`stat-value ${balanceColor(wallet?.balanceCredits ?? 0)}`}>
                            {(wallet?.balanceCredits ?? 0).toLocaleString()}
                        </div>
                        <div className="stat-desc">credits available</div>
                    </div>

                    {/* Negative Limit */}
                    <div className="stat">
                        <div className="stat-title">Negative Limit</div>
                        <div className="stat-value text-base-content/70 text-2xl">
                            {(wallet?.negativeLimitCredits ?? 0).toLocaleString()}
                        </div>
                        <div className="stat-desc">minimum allowed balance</div>
                    </div>

                    {/* Status */}
                    <div className="stat">
                        <div className="stat-title">Account Status</div>
                        <div className="stat-value text-2xl mt-2">
                            {statusBadge(wallet?.status)}
                        </div>
                    </div>

                    {/* Can Spend */}
                    <div className="stat">
                        <div className="stat-title">AI Access</div>
                        <div className="stat-value text-2xl mt-2">
                            {canSpend?.allowed ? (
                                <span className="badge badge-success badge-lg gap-1">
                                    <CheckCircleIcon /> Allowed
                                </span>
                            ) : (
                                <span className="badge badge-error badge-lg gap-1">
                                    <XCircleIcon /> Blocked
                                </span>
                            )}
                        </div>
                        {!canSpend?.allowed && canSpend?.reason && (
                            <div className="stat-desc text-error capitalize">
                                {canSpend.reason.replace(/_/g, ' ')}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Can Spend Alert ── */}
                {!canSpend?.allowed && (
                    <div className="alert alert-error shadow">
                        <XCircleIcon />
                        <div>
                            <h3 className="font-bold">AI requests are currently blocked</h3>
                            <div className="text-sm">
                                Reason: <span className="font-medium capitalize">{canSpend?.reason?.replace(/_/g, ' ')}</span>
                                {' — '}Current balance: <span className="font-mono font-bold">{canSpend?.currentBalance}</span> credits.
                                Add credits below to restore access.
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Add Credits Card ── */}
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title">Add Credits</h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Credits are used to pay for AI requests. Each request deducts credits based on the model and token usage.
                        </p>

                        <form onSubmit={handleCredit} className="flex flex-col sm:flex-row gap-3 items-start">
                            <div className="form-control flex-1">
                                <label className="label">
                                    <span className="label-text">Number of Credits</span>
                                </label>
                                <div className="join w-full">
                                    <input
                                        id="credit-amount-input"
                                        type="number"
                                        min="1"
                                        max="100000"
                                        placeholder="e.g. 100"
                                        className="input input-bordered join-item flex-1"
                                        value={creditAmount}
                                        onChange={(e) => setCreditAmount(e.target.value)}
                                        disabled={creditLoading}
                                    />
                                    <button
                                        id="add-credits-btn"
                                        type="submit"
                                        className={`btn btn-primary join-item ${creditLoading ? 'loading' : ''}`}
                                        disabled={creditLoading || !creditAmount}
                                    >
                                        {creditLoading ? 'Adding…' : 'Add Credits'}
                                    </button>
                                </div>
                            </div>
                        </form>

                        {/* Quick amounts */}
                        <div className="flex gap-2 flex-wrap mt-2">
                            <span className="text-xs text-base-content/50 self-center">Quick add:</span>
                            {[50, 100, 500, 1000].map((amt) => (
                                <button
                                    key={amt}
                                    className="btn btn-xs btn-outline"
                                    onClick={() => setCreditAmount(String(amt))}
                                    disabled={creditLoading}
                                >
                                    +{amt}
                                </button>
                            ))}
                        </div>

                        {/* Feedback */}
                        {creditSuccess && (
                            <div className="alert alert-success mt-3">
                                <CheckCircleIcon />
                                <span>{creditSuccess}</span>
                            </div>
                        )}
                        {creditError && (
                            <div className="alert alert-error mt-3">
                                <XCircleIcon />
                                <span>{creditError}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Info Card ── */}
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-base">How Credits Work</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                            <div className="flex gap-3">
                                <div className="text-2xl">💬</div>
                                <div>
                                    <div className="font-semibold text-sm">Per AI Request</div>
                                    <div className="text-xs text-base-content/60">Credits are deducted based on tokens used × model price × exchange rate</div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="text-2xl">📉</div>
                                <div>
                                    <div className="font-semibold text-sm">Negative Balance</div>
                                    <div className="text-xs text-base-content/60">Requests are blocked when balance reaches {wallet?.negativeLimitCredits ?? -50} credits</div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="text-2xl">🔒</div>
                                <div>
                                    <div className="font-semibold text-sm">Idempotent Billing</div>
                                    <div className="text-xs text-base-content/60">Each AI request is billed exactly once, even on retries</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </DashboardLayout>
    );
};

export default AiWallet;
